import { transformToProcedurePayload } from 'clickNgo/helpers/api'
import { Nullable } from 'cng-common/models/utils'

import { CampaignCallingList, CampaignExtendedCallingList } from '../../model/campaign'
import { campaignBaseApi } from '../campaignBaseApi'
import {
  buildCallingListCreateParams,
  buildCallingListUpdateParams,
  mapCallingListFromRawData,
  mapExtendedCallingListFromRawData,
} from '../mapping'
import {
  CampaignApiTags,
  CreateCallingListParams,
  DeleteCallingListByCampaignParams,
  DeleteCallingListRecordParams,
  GetCampaignExtendedCallingListParams,
  UpdateCallingListParams,
} from '../models'
import {
  campaignProcedureUrl,
  SP_LUT_CALLING_LIST_CREATE_FROM_FILE,
  SP_LUT_CALLING_LIST_RECORD_DELETE,
  SP_LUT_CALLING_LIST_UPDATE,
  SP_LUT_CAMPAIGN_CALLING_LIST_DELETE,
  SP_LUT_CAMPAIGN_CALLING_LIST_GET_EXTENDED,
} from '../procedures'
import { mapRows } from '../responseMappers'

const callingListEndpoints = campaignBaseApi.injectEndpoints({
  endpoints: (build) => ({
    // Fetch campaign extended calling list
    // This endpoint retrieves an extended calling list for a specific campaign.
    // It includes additional details about each entry in the calling list.
    getCampaignCallingListExtended: build.query<
      { items: CampaignExtendedCallingList[]; total: number },
      GetCampaignExtendedCallingListParams
    >({
      transformResponse(response) {
        return {
          items: response.Data.map(mapExtendedCallingListFromRawData),
          total: response.Total,
        }
      },

      providesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignCallingList, id: params.campaignId },
      ],

      query({ page, pageSize, ...rest }) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_CALLING_LIST_GET_EXTENDED),
          method: 'POST',
          data: {
            ...transformToProcedurePayload(rest),
            page,
            pageSize,
          },
        }
      },
    }),

    // Create calling list
    // This endpoint creates a new calling list for a specific campaign.
    createCallingList: build.mutation<CampaignCallingList[], CreateCallingListParams>({
      query(params) {
        return {
          url: campaignProcedureUrl(SP_LUT_CALLING_LIST_CREATE_FROM_FILE),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          data: buildCallingListCreateParams(params),
        }
      },

      // Only invalidate when suppressInvalidation !== true — batched/internal
      // calls must not trigger a refetch of the visible calling list.
      invalidatesTags: (_result, _error, params) =>
        params.suppressInvalidation
          ? []
          : [{ type: CampaignApiTags.CampaignCallingList, id: params.campaignId }],

      transformResponse: mapRows(mapCallingListFromRawData),
    }),

    // Update calling list
    // This endpoint updates the visibility status of a specific entry in the calling list.
    updateCallingList: build.mutation<Nullable<CampaignCallingList[]>, UpdateCallingListParams>({
      query(params) {
        return {
          url: campaignProcedureUrl(SP_LUT_CALLING_LIST_UPDATE),
          method: 'POST',
          data: buildCallingListUpdateParams(params),
        }
      },

      transformResponse(response) {
        if (response.Data && response.Data.length > 0) {
          return response.Data.map(mapCallingListFromRawData)
        }

        return null
      },

      invalidatesTags: (_result, _error, params) =>
        params.suppressInvalidation
          ? []
          : params.campaignId
            ? [{ type: CampaignApiTags.CampaignCallingList, id: params.campaignId }]
            : [],
    }),

    // Delete a single calling list record by id
    // This endpoint deletes a specific calling list record via the spLutCallingListRecordDelete procedure.
    deleteCallingListRecord: build.mutation<boolean, DeleteCallingListRecordParams>({
      query({ entryId }) {
        return {
          url: campaignProcedureUrl(SP_LUT_CALLING_LIST_RECORD_DELETE),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          data: {
            'p.EntryId': entryId,
          },
        }
      },

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignCallingList, id: params.campaignId },
      ],

      transformResponse() {
        return true
      },
    }),

    // Delete calling list by campaign id
    // This endpoint deletes all calling list entries for a specific campaign.
    deleteCallingListByCampaignId: build.mutation<boolean, DeleteCallingListByCampaignParams>({
      query({ campaignId }) {
        return {
          url: campaignProcedureUrl(SP_LUT_CAMPAIGN_CALLING_LIST_DELETE),
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          data: {
            'p.CampaignId': campaignId,
          },
        }
      },

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignCallingList, id: params.campaignId },
      ],

      transformResponse() {
        return true
      },
    }),
  }),
})

export const {
  useGetCampaignCallingListExtendedQuery,
  useCreateCallingListMutation,
  useUpdateCallingListMutation,
  useDeleteCallingListRecordMutation,
  useDeleteCallingListByCampaignIdMutation,
} = callingListEndpoints
